import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
    Building2,
    Mail,
    Phone,
    Globe,
    Shield,
    Bell,
    User,
    Save,
    AlertCircle,
    Loader2,
    Lock,
    Eye,
    EyeOff,
    Camera,
    Upload,
    Briefcase,
    Sun,
    Moon,
    Monitor,
    Image,
    Trash2,
    FileText,
    Copy,
    Star,
    CreditCard,
    ExternalLink,
    Plus,
    MoreVertical,
} from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const AdminSettingsPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user, updateUser } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'profile');

    // My Profile State
    const [profileData, setProfileData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        position: '',
        department: '',
    });

    // Company Profile State
    const [companyProfile, setCompanyProfile] = useState({
        company_name: '',
        company_website: '',
        company_phone: '',
        industry: '',
        company_size: '',
    });

    // Notification Settings State
    const [notificationSettings, setNotificationSettings] = useState({
        email_task_completion: true,
        email_new_employee: true,
        email_document_signed: true,
        email_weekly_summary: false,
    });

    // Security State
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false,
    });

    // Branding State
    const [branding, setBranding] = useState({ logo_url: null, show_logo_on_cards: true });
    const [logoUploading, setLogoUploading] = useState(false);
    const [logoPreview, setLogoPreview] = useState(null);

    // Billing / Subscription State (owner-only)
    const [billingData, setBillingData] = useState(null);
    const [billingLoading, setBillingLoading] = useState(false);
    const [portalLoading, setPortalLoading] = useState(false);

    useEffect(() => {
        fetchSettings();
        fetchBranding();
    }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);

            // Fetch user profile
            const meResponse = await api.get('/auth/me');
            const userData = meResponse.data.user;

            setProfileData({
                first_name: userData.first_name || '',
                last_name: userData.last_name || '',
                email: userData.email || '',
                phone: userData.phone || '',
                position: userData.position || '',
                department: userData.department || '',
            });

            // Fetch company profile
            const companyResponse = await api.get('/auth/profile');
            const company = companyResponse.data.company;

            setCompanyProfile({
                company_name: company.company_name || '',
                company_website: company.company_website || '',
                company_phone: company.company_phone || '',
                industry: company.industry || '',
                company_size: company.company_size || '',
            });

            // Load notification settings if available
            if (company.settings?.notifications) {
                setNotificationSettings((prev) => ({
                    ...prev,
                    ...company.settings.notifications,
                }));
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
            toast.error('Failed to load settings');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveProfile = async () => {
        setSaving(true);
        try {
            await api.put('/companies/profile', {
                first_name: profileData.first_name,
                last_name: profileData.last_name,
                phone: profileData.phone,
                position: profileData.position,
                department: profileData.department,
            });
            toast.success('Profile updated successfully!');
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to update profile');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveCompanyProfile = async () => {
        setSaving(true);
        try {
            await api.put('/companies/profile', companyProfile);
            toast.success('Company profile updated successfully!');
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to update profile');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveNotificationSettings = async () => {
        setSaving(true);
        try {
            await api.put('/companies/settings/notifications', notificationSettings);
            toast.success('Notification preferences updated!');
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to update settings');
        } finally {
            setSaving(false);
        }
    };

    // ─── Branding Handlers ─────────────────────────────
    const fetchBranding = async () => {
        try {
            const response = await api.get('/company/branding');
            const company = response.data.company;
            setBranding({
                logo_url: company.logo_url || null,
                show_logo_on_cards: company.show_logo_on_cards ?? true,
            });
        } catch (err) {
            // Silent fail
        }
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Client-side validation
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/svg+xml'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Please select a JPG, PNG, WebP, or SVG image');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Image must be less than 5MB');
            return;
        }

        // Show preview
        const reader = new FileReader();
        reader.onload = (ev) => setLogoPreview(ev.target.result);
        reader.readAsDataURL(file);

        // Upload
        setLogoUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await api.post('/company/logo', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setBranding(prev => ({ ...prev, logo_url: response.data.logo_url }));
            setLogoPreview(null);
            toast.success('Company logo uploaded!');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to upload logo');
            setLogoPreview(null);
        } finally {
            setLogoUploading(false);
        }
    };

    const handleRemoveLogo = async () => {
        try {
            await api.delete('/company/logo');
            setBranding(prev => ({ ...prev, logo_url: null }));
            setLogoPreview(null);
            toast.success('Logo removed');
        } catch (err) {
            toast.error('Failed to remove logo');
        }
    };

    const handleToggleShowLogo = async () => {
        const newValue = !branding.show_logo_on_cards;
        setBranding(prev => ({ ...prev, show_logo_on_cards: newValue }));
        try {
            await api.put('/company/branding', { show_logo_on_cards: newValue });
        } catch (err) {
            setBranding(prev => ({ ...prev, show_logo_on_cards: !newValue }));
            toast.error('Failed to update setting');
        }
    };

    // ─── Billing Handlers (Owner Only) ────────────────
    const fetchBillingData = async () => {
        setBillingLoading(true);
        try {
            const res = await api.get('/billing/subscription');
            setBillingData(res.data.subscription);
        } catch (err) {
            // Not owner or no subscription — silently skip
            setBillingData(null);
        } finally {
            setBillingLoading(false);
        }
    };

    const handleOpenPortal = async (flow) => {
        setPortalLoading(true);
        try {
            const res = await api.post('/billing/portal', {
                returnUrl: window.location.href,
            });
            if (res.data.url) {
                window.location.href = res.data.url;
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to open billing portal');
        } finally {
            setPortalLoading(false);
        }
    };

    const handleChangePassword = async () => {
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            toast.error('New passwords do not match');
            return;
        }
        if (passwordData.newPassword.length < 8) {
            toast.error('Password must be at least 8 characters');
            return;
        }
        setSaving(true);
        try {
            await api.put('/auth/change-password', {
                currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword,
            });
            toast.success('Password updated successfully!');
            setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to change password');
        } finally {
            setSaving(false);
        }
    };

    const industries = [
        'Technology', 'Healthcare', 'Finance', 'Education',
        'Retail', 'Manufacturing', 'Consulting', 'Other',
    ];
    const companySizes = ['1-10', '11-50', '51-100', '101-500', '500+'];

    const tabs = [
        { id: 'profile', label: 'My Profile', icon: User },
        { id: 'company', label: 'Company', icon: Building2 },
        { id: 'security', label: 'Security', icon: Shield },
        { id: 'notifications', label: 'Notifications', icon: Bell },
        { id: 'templates', label: 'Email Templates', icon: FileText },
    ];

    // ─── Email Templates State ──────────────────────
    const [templates, setTemplates] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);

    const fetchTemplates = async () => {
        setTemplatesLoading(true);
        try {
            const res = await api.get('/email-templates');
            setTemplates(res.data.templates || []);
        } catch (error) {
            toast.error('Failed to load templates');
        } finally {
            setTemplatesLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'templates') {
            fetchTemplates();
        }
        if (activeTab === 'company' && user?.is_company_creator && !billingData) {
            fetchBillingData();
        }
    }, [activeTab]);

    const handleDeleteTemplate = async (id) => {
        if (!window.confirm('Are you sure you want to delete this template?')) return;
        try {
            await api.delete(`/email-templates/${id}`);
            toast.success('Template deleted');
            fetchTemplates();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to delete template');
        }
    };

    const handleDuplicateTemplate = async (id) => {
        try {
            await api.post(`/email-templates/${id}/duplicate`);
            toast.success('Template duplicated');
            fetchTemplates();
        } catch (error) {
            toast.error('Failed to duplicate template');
        }
    };

    const handleSetDefault = async (id) => {
        try {
            await api.put(`/email-templates/${id}/default`);
            toast.success('Default template updated');
            fetchTemplates();
        } catch (error) {
            toast.error('Failed to set default');
        }
    };

    // ─── Reusable Toggle Component ──────────────────
    const Toggle = ({ enabled, onChange, label, description, badge }) => (
        <div className="flex items-center justify-between p-4 bg-secondary rounded-xl">
            <div className="flex-1">
                <div className="flex items-center gap-2 mb-0.5">
                    <p className="font-medium text-foreground">{label}</p>
                    {badge && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-info/10 text-info rounded">
                            {badge}
                        </span>
                    )}
                </div>
                {description && <p className="text-sm text-muted-foreground">{description}</p>}
            </div>
            <button
                onClick={onChange}
                className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ml-4 ${enabled ? 'bg-accent' : 'bg-muted'
                    }`}
            >
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform shadow-sm ${enabled ? 'left-7' : 'left-1'
                    }`} />
            </button>
        </div>
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin no-theme-transition rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="p-4 lg:p-6">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-foreground">Settings</h1>
                <p className="text-muted-foreground mt-1">Manage your profile and preferences</p>
            </div>

            {/* Tabs */}
            <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
                <div className="border-b border-border">
                    <nav className="flex overflow-x-auto">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === tab.id
                                    ? 'border-accent text-accent'
                                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                                    }`}
                            >
                                <tab.icon size={18} />
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>

                <div className="p-6">
                    {/* ─── My Profile Tab ─────────────────────────── */}
                    {activeTab === 'profile' && (
                        <div className="space-y-8">
                            {/* Profile Information Section */}
                            <div>
                                <h3 className="text-lg font-semibold text-foreground mb-1">Profile Information</h3>
                                <p className="text-sm text-muted-foreground mb-6">Update your personal details visible to other team members.</p>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-2">
                                            First Name
                                        </label>
                                        <input
                                            type="text"
                                            value={profileData.first_name}
                                            onChange={(e) => setProfileData({ ...profileData, first_name: e.target.value })}
                                            className="w-full px-4 py-3 bg-card border border-border rounded-xl focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none text-foreground placeholder:text-muted-foreground"
                                            placeholder="First name"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-2">
                                            Last Name
                                        </label>
                                        <input
                                            type="text"
                                            value={profileData.last_name}
                                            onChange={(e) => setProfileData({ ...profileData, last_name: e.target.value })}
                                            className="w-full px-4 py-3 bg-card border border-border rounded-xl focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none text-foreground placeholder:text-muted-foreground"
                                            placeholder="Last name"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-2">
                                            Email Address
                                        </label>
                                        <div className="relative">
                                            <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                            <input
                                                type="email"
                                                value={profileData.email}
                                                readOnly
                                                className="w-full pl-10 pr-4 py-3 bg-secondary border border-border rounded-xl outline-none text-muted-foreground cursor-not-allowed"
                                            />
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-1">Email cannot be changed</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-2">
                                            Phone Number
                                        </label>
                                        <div className="relative">
                                            <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                            <input
                                                type="tel"
                                                value={profileData.phone}
                                                onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                                                className="w-full pl-10 pr-4 py-3 bg-card border border-border rounded-xl focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none text-foreground placeholder:text-muted-foreground"
                                                placeholder="+1 (555) 000-0000"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-2">
                                            Job Title
                                        </label>
                                        <div className="relative">
                                            <Briefcase size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                            <input
                                                type="text"
                                                value={profileData.position}
                                                onChange={(e) => setProfileData({ ...profileData, position: e.target.value })}
                                                className="w-full pl-10 pr-4 py-3 bg-card border border-border rounded-xl focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none text-foreground placeholder:text-muted-foreground"
                                                placeholder="e.g. HR Manager"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-2">
                                            Department
                                        </label>
                                        <input
                                            type="text"
                                            value={profileData.department}
                                            onChange={(e) => setProfileData({ ...profileData, department: e.target.value })}
                                            className="w-full px-4 py-3 bg-card border border-border rounded-xl focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none text-foreground placeholder:text-muted-foreground"
                                            placeholder="e.g. Human Resources"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Divider */}
                            <hr className="border-border" />

                            {/* Display Preferences Section */}
                            <div>
                                <h3 className="text-lg font-semibold text-foreground mb-1">Display Preferences</h3>
                                <p className="text-sm text-muted-foreground mb-6">Customize how Envion looks for you.</p>

                                <div className="max-w-lg">
                                    <label className="block text-sm font-medium text-foreground mb-3">Theme</label>
                                    <div className="grid grid-cols-2 gap-3">
                                        {/* Light Mode Card */}
                                        <button
                                            onClick={() => { if (theme === 'dark') toggleTheme(); }}
                                            className={`relative p-4 rounded-xl border-2 transition-all text-left ${theme === 'light'
                                                ? 'border-accent bg-accent/5 shadow-sm'
                                                : 'border-border hover:border-muted-foreground/30 bg-card'
                                                }`}
                                        >
                                            <div className="flex items-center gap-3 mb-3">
                                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${theme === 'light' ? 'bg-accent/10 text-accent' : 'bg-secondary text-muted-foreground'}`}>
                                                    <Sun size={20} />
                                                </div>
                                                <div>
                                                    <p className="font-semibold text-foreground text-sm">Light</p>
                                                    <p className="text-xs text-muted-foreground">Classic bright interface</p>
                                                </div>
                                            </div>
                                            {/* Theme Preview */}
                                            <div className="rounded-lg overflow-hidden border border-border">
                                                <div className="h-3 bg-[#faf7f3]"></div>
                                                <div className="flex h-12">
                                                    <div className="w-6 bg-[#2a3f54]"></div>
                                                    <div className="flex-1 bg-[#faf7f3] p-1.5 space-y-1">
                                                        <div className="h-1.5 w-8 bg-[#e5e1dd] rounded"></div>
                                                        <div className="h-1.5 w-12 bg-[#e5e1dd] rounded"></div>
                                                    </div>
                                                </div>
                                            </div>
                                            {theme === 'light' && (
                                                <div className="absolute top-3 right-3 w-5 h-5 bg-accent text-white rounded-full flex items-center justify-center">
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="20 6 9 17 4 12" />
                                                    </svg>
                                                </div>
                                            )}
                                        </button>

                                        {/* Dark Mode Card */}
                                        <button
                                            onClick={() => { if (theme === 'light') toggleTheme(); }}
                                            className={`relative p-4 rounded-xl border-2 transition-all text-left ${theme === 'dark'
                                                ? 'border-accent bg-accent/5 shadow-sm'
                                                : 'border-border hover:border-muted-foreground/30 bg-card'
                                                }`}
                                        >
                                            <div className="flex items-center gap-3 mb-3">
                                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${theme === 'dark' ? 'bg-accent/10 text-accent' : 'bg-secondary text-muted-foreground'}`}>
                                                    <Moon size={20} />
                                                </div>
                                                <div>
                                                    <p className="font-semibold text-foreground text-sm">Dark</p>
                                                    <p className="text-xs text-muted-foreground">Easy on the eyes</p>
                                                </div>
                                            </div>
                                            {/* Theme Preview */}
                                            <div className="rounded-lg overflow-hidden border border-border">
                                                <div className="h-3 bg-[#1a2332]"></div>
                                                <div className="flex h-12">
                                                    <div className="w-6 bg-[#2a3f54]"></div>
                                                    <div className="flex-1 bg-[#1a2332] p-1.5 space-y-1">
                                                        <div className="h-1.5 w-8 bg-[#334155] rounded"></div>
                                                        <div className="h-1.5 w-12 bg-[#334155] rounded"></div>
                                                    </div>
                                                </div>
                                            </div>
                                            {theme === 'dark' && (
                                                <div className="absolute top-3 right-3 w-5 h-5 bg-accent text-white rounded-full flex items-center justify-center">
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                        <polyline points="20 6 9 17 4 12" />
                                                    </svg>
                                                </div>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end pt-2">
                                <button
                                    onClick={handleSaveProfile}
                                    disabled={saving}
                                    className="px-6 py-3 bg-accent text-white rounded-xl font-semibold shadow-lg shadow-accent/30 hover:shadow-xl hover:shadow-accent/40 transition-all disabled:opacity-50 flex items-center gap-2"
                                >
                                    {saving ? <Loader2 size={20} className="animate-spin no-theme-transition" /> : <Save size={20} />}
                                    Save Changes
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ─── Company Tab ────────────────────────────── */}
                    {activeTab === 'company' && (
                        <div className="space-y-8">
                            {/* Role Badge */}
                            <div className="flex items-center gap-3 p-4 bg-accent/5 border border-accent/20 rounded-xl">
                                <div className="w-10 h-10 bg-accent/10 text-accent rounded-lg flex items-center justify-center">
                                    <Shield size={20} />
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-foreground">
                                        {user?.is_company_creator ? 'Owner' : user?.role_name || 'Administrator'}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        {user?.is_company_creator
                                            ? 'Full access to all company settings'
                                            : 'Limited access based on your role permissions'}
                                    </p>
                                </div>
                            </div>

                            {/* Company Information */}
                            <div>
                                <h3 className="text-lg font-semibold text-foreground mb-1">Company Information</h3>
                                <p className="text-sm text-muted-foreground mb-6">Basic details about your organization.</p>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-2">
                                            Company Name
                                        </label>
                                        <div className="relative">
                                            <Building2 size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                            <input
                                                type="text"
                                                value={companyProfile.company_name}
                                                onChange={(e) => setCompanyProfile({ ...companyProfile, company_name: e.target.value })}
                                                className="w-full pl-10 pr-4 py-3 bg-card border border-border rounded-xl focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none text-foreground placeholder:text-muted-foreground"
                                                placeholder="Your Company Name"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-2">
                                            Website
                                        </label>
                                        <div className="relative">
                                            <Globe size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                            <input
                                                type="url"
                                                value={companyProfile.company_website}
                                                onChange={(e) => setCompanyProfile({ ...companyProfile, company_website: e.target.value })}
                                                className="w-full pl-10 pr-4 py-3 bg-card border border-border rounded-xl focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none text-foreground placeholder:text-muted-foreground"
                                                placeholder="https://yourcompany.com"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-2">
                                            Phone Number
                                        </label>
                                        <div className="relative">
                                            <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                            <input
                                                type="tel"
                                                value={companyProfile.company_phone}
                                                onChange={(e) => setCompanyProfile({ ...companyProfile, company_phone: e.target.value })}
                                                className="w-full pl-10 pr-4 py-3 bg-card border border-border rounded-xl focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none text-foreground placeholder:text-muted-foreground"
                                                placeholder="+1 (555) 000-0000"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-2">
                                            Industry
                                        </label>
                                        <div className="relative">
                                            <Briefcase size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                            <select
                                                value={companyProfile.industry}
                                                onChange={(e) => setCompanyProfile({ ...companyProfile, industry: e.target.value })}
                                                className="w-full pl-10 pr-4 py-3 bg-card border border-border rounded-xl focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none text-foreground appearance-none"
                                            >
                                                <option value="">Select industry</option>
                                                {industries.map((ind) => (
                                                    <option key={ind} value={ind}>{ind}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-2">
                                            Company Size
                                        </label>
                                        <select
                                            value={companyProfile.company_size}
                                            onChange={(e) => setCompanyProfile({ ...companyProfile, company_size: e.target.value })}
                                            className="w-full px-4 py-3 bg-card border border-border rounded-xl focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none text-foreground appearance-none"
                                        >
                                            <option value="">Select size</option>
                                            {companySizes.map((size) => (
                                                <option key={size} value={size}>{size} employees</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end pt-2">
                                <button
                                    onClick={handleSaveCompanyProfile}
                                    disabled={saving}
                                    className="px-6 py-3 bg-accent text-white rounded-xl font-semibold shadow-lg shadow-accent/30 hover:shadow-xl hover:shadow-accent/40 transition-all disabled:opacity-50 flex items-center gap-2"
                                >
                                    {saving ? <Loader2 size={20} className="animate-spin no-theme-transition" /> : <Save size={20} />}
                                    Save Changes
                                </button>
                            </div>

                            {/* ─── Company Branding (Owner Only) ───────────── */}
                            {user?.is_company_creator && (
                                <div className="border-t border-border pt-8">
                                    <h3 className="text-lg font-semibold text-foreground mb-1">Company Branding</h3>
                                    <p className="text-sm text-muted-foreground mb-6">Upload your company logo and configure branding options.</p>

                                    {/* Logo Upload Area */}
                                    <div className="flex items-start gap-6 mb-6">
                                        {/* Preview */}
                                        <div className="relative group">
                                            <div className="w-24 h-24 rounded-2xl border-2 border-dashed border-border bg-secondary flex items-center justify-center overflow-hidden">
                                                {logoPreview || branding.logo_url ? (
                                                    <img
                                                        src={logoPreview || branding.logo_url}
                                                        alt="Company logo"
                                                        className="w-full h-full object-cover rounded-2xl"
                                                    />
                                                ) : (
                                                    <Image size={32} className="text-muted-foreground" />
                                                )}
                                                {logoUploading && (
                                                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center rounded-2xl">
                                                        <Loader2 size={24} className="text-white animate-spin no-theme-transition" />
                                                    </div>
                                                )}
                                            </div>
                                            {branding.logo_url && !logoUploading && (
                                                <button
                                                    onClick={handleRemoveLogo}
                                                    className="absolute -top-2 -right-2 w-6 h-6 bg-destructive text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                                                    title="Remove logo"
                                                >
                                                    <Trash2 size={12} />
                                                </button>
                                            )}
                                        </div>

                                        {/* Upload Controls */}
                                        <div className="flex-1">
                                            <label
                                                className="inline-flex items-center gap-2 px-4 py-2.5 bg-card border border-border rounded-xl text-sm font-medium text-foreground hover:bg-secondary transition-colors cursor-pointer"
                                            >
                                                <Upload size={16} />
                                                {branding.logo_url ? 'Change Logo' : 'Upload Logo'}
                                                <input
                                                    type="file"
                                                    accept="image/jpeg,image/png,image/webp,image/svg+xml"
                                                    onChange={handleLogoUpload}
                                                    className="hidden"
                                                    disabled={logoUploading}
                                                />
                                            </label>
                                            <p className="text-xs text-muted-foreground mt-2">
                                                JPG, PNG, WebP or SVG. Max 5MB. Recommended: 200×200px.
                                            </p>
                                        </div>
                                    </div>

                                    {/* Show Logo on Cards Toggle */}
                                    <Toggle
                                        enabled={branding.show_logo_on_cards}
                                        onChange={handleToggleShowLogo}
                                        label="Show logo on employee cards"
                                        description="Display your company logo on employee profile cards and badges."
                                    />
                                </div>
                            )}

                            {/* ─── Subscription & Billing (Owner Only) ────── */}
                            {user?.is_company_creator && (
                                <div className="border-t border-border pt-8">
                                    <h3 className="text-lg font-semibold text-foreground mb-1">Subscription & Billing</h3>
                                    <p className="text-sm text-muted-foreground mb-6">Manage your plan, payment methods, and invoices.</p>

                                    {billingLoading ? (
                                        <div className="flex items-center gap-3 text-muted-foreground text-sm py-8 justify-center">
                                            <Loader2 size={20} className="animate-spin no-theme-transition" />
                                            Loading billing details...
                                        </div>
                                    ) : billingData ? (
                                        <div className="space-y-6">
                                            {/* Status + Plan Summary */}
                                            <div className="bg-secondary rounded-xl p-5">
                                                <div className="flex items-center justify-between mb-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 bg-accent/10 text-accent rounded-lg flex items-center justify-center">
                                                            <CreditCard size={20} />
                                                        </div>
                                                        <div>
                                                            <p className="font-semibold text-foreground">{billingData.companyName}</p>
                                                            <p className="text-xs text-muted-foreground">
                                                                {billingData.billingCycle === 'annual' ? 'Annual' : 'Monthly'} Plan
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide ${billingData.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-400' :
                                                            billingData.status === 'past_due' ? 'bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-400' :
                                                                billingData.status === 'cancelled' ? 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400' :
                                                                    'bg-gray-100 text-gray-600 dark:bg-gray-500/15 dark:text-gray-400'
                                                        }`}>
                                                        {billingData.status === 'past_due' ? 'Past Due' : billingData.status}
                                                    </span>
                                                </div>

                                                {billingData.cancelAtPeriodEnd && (
                                                    <div className="bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/20 rounded-lg p-3 mb-4 text-sm text-orange-700 dark:text-orange-300">
                                                        ⚠️ Your subscription is set to cancel at the end of the current billing period.
                                                    </div>
                                                )}

                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                    <div>
                                                        <p className="text-xs text-muted-foreground mb-1">Price</p>
                                                        <p className="text-sm font-semibold text-foreground">
                                                            £{billingData.monthlyPrice?.toLocaleString()}
                                                            {billingData.billingCycle === 'annual' ? '/year' : '/month'}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs text-muted-foreground mb-1">Next Billing</p>
                                                        <p className="text-sm font-semibold text-foreground">
                                                            {billingData.nextBillingDate
                                                                ? new Date(billingData.nextBillingDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                                                                : '—'}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs text-muted-foreground mb-1">Admins</p>
                                                        <p className="text-sm font-semibold text-foreground">
                                                            {billingData.usage?.activeAdmins ?? 0} / {billingData.maxActiveAdmins || '∞'}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs text-muted-foreground mb-1">Onboarded (this month)</p>
                                                        <p className="text-sm font-semibold text-foreground">
                                                            {billingData.usage?.onboardedThisMonth ?? 0} / {billingData.maxOnboardersMonth || '∞'}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Billing Actions */}
                                            {billingData.hasStripeCustomer && (
                                                <div className="flex flex-wrap gap-3">
                                                    <button
                                                        onClick={() => handleOpenPortal('payment_method_update')}
                                                        disabled={portalLoading}
                                                        className="flex items-center gap-2 px-4 py-2.5 bg-card border border-border rounded-xl text-sm font-medium text-foreground hover:bg-secondary transition-colors disabled:opacity-50"
                                                    >
                                                        <CreditCard size={16} />
                                                        Update Payment Method
                                                        <ExternalLink size={14} className="text-muted-foreground" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleOpenPortal('invoice_history')}
                                                        disabled={portalLoading}
                                                        className="flex items-center gap-2 px-4 py-2.5 bg-card border border-border rounded-xl text-sm font-medium text-foreground hover:bg-secondary transition-colors disabled:opacity-50"
                                                    >
                                                        <FileText size={16} />
                                                        View Invoices
                                                        <ExternalLink size={14} className="text-muted-foreground" />
                                                    </button>
                                                    {billingData.status === 'active' && !billingData.cancelAtPeriodEnd && (
                                                        <button
                                                            onClick={() => handleOpenPortal('subscription_cancel')}
                                                            disabled={portalLoading}
                                                            className="flex items-center gap-2 px-4 py-2.5 bg-card border border-destructive/30 rounded-xl text-sm font-medium text-destructive hover:bg-destructive/5 transition-colors disabled:opacity-50"
                                                        >
                                                            Cancel Subscription
                                                            <ExternalLink size={14} />
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="text-center py-8 text-muted-foreground text-sm">
                                            <CreditCard size={32} className="mx-auto mb-3 opacity-40" />
                                            <p>No billing information available.</p>
                                            <p className="text-xs mt-1">Billing details will appear here once your subscription is active.</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ─── Security Tab ───────────────────────────── */}
                    {activeTab === 'security' && (
                        <div className="space-y-8">
                            <div>
                                <h3 className="text-lg font-semibold text-foreground mb-1">Change Password</h3>
                                <p className="text-sm text-muted-foreground mb-6">Update your password to keep your account secure.</p>

                                <div className="space-y-4 max-w-md">
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-2">
                                            Current Password
                                        </label>
                                        <div className="relative">
                                            <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                            <input
                                                type={showPasswords.current ? 'text' : 'password'}
                                                value={passwordData.currentPassword}
                                                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                                                className="w-full pl-10 pr-12 py-3 bg-card border border-border rounded-xl focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none text-foreground placeholder:text-muted-foreground"
                                                placeholder="••••••••"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                            >
                                                {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </button>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-2">
                                            New Password
                                        </label>
                                        <div className="relative">
                                            <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                            <input
                                                type={showPasswords.new ? 'text' : 'password'}
                                                value={passwordData.newPassword}
                                                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                                                className="w-full pl-10 pr-12 py-3 bg-card border border-border rounded-xl focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none text-foreground placeholder:text-muted-foreground"
                                                placeholder="••••••••"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                            >
                                                {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </button>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-foreground mb-2">
                                            Confirm New Password
                                        </label>
                                        <div className="relative">
                                            <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                            <input
                                                type={showPasswords.confirm ? 'text' : 'password'}
                                                value={passwordData.confirmPassword}
                                                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                                                className="w-full pl-10 pr-12 py-3 bg-card border border-border rounded-xl focus:ring-2 focus:ring-accent/50 focus:border-accent outline-none text-foreground placeholder:text-muted-foreground"
                                                placeholder="••••••••"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                            >
                                                {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </button>
                                        </div>
                                    </div>
                                    <button
                                        onClick={handleChangePassword}
                                        disabled={saving || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                                        className="px-6 py-3 bg-primary text-primary-foreground rounded-xl font-semibold hover:opacity-90 transition-all disabled:opacity-50 flex items-center gap-2"
                                    >
                                        {saving ? <Loader2 size={20} className="animate-spin no-theme-transition" /> : <Lock size={20} />}
                                        Update Password
                                    </button>
                                </div>
                            </div>

                            <hr className="border-border" />

                            {/* Account Information */}
                            <div>
                                <h3 className="text-lg font-semibold text-foreground mb-4">Account Information</h3>
                                <div className="p-6 bg-secondary rounded-xl">
                                    <div className="grid grid-cols-2 gap-6 text-sm">
                                        <div>
                                            <p className="text-muted-foreground mb-1">Account Created</p>
                                            <p className="font-medium text-foreground">
                                                {user?.created_at
                                                    ? new Date(user.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                                                    : 'N/A'}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-muted-foreground mb-1">Role</p>
                                            <p className="font-medium text-foreground">
                                                {user?.is_company_creator ? 'Owner' : user?.role_name || 'Administrator'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ─── Notifications Tab ──────────────────────── */}
                    {activeTab === 'notifications' && (
                        <div className="space-y-6">
                            <div>
                                <h3 className="text-lg font-semibold text-foreground mb-1">Email Notifications</h3>
                                <p className="text-sm text-muted-foreground mb-6">Choose which notifications you'd like to receive.</p>

                                <div className="space-y-3">
                                    {[
                                        { key: 'email_task_completion', label: 'Task Completions', description: 'Get notified when an employee completes a task', badge: 'Recommended' },
                                        { key: 'email_new_employee', label: 'New Employee Added', description: 'Get notified when a new employee is created' },
                                        { key: 'email_document_signed', label: 'Document Signed', description: 'Get notified when an employee signs a document' },
                                        { key: 'email_weekly_summary', label: 'Weekly Summary', description: 'Receive a weekly onboarding progress report' },
                                    ].map((item) => (
                                        <Toggle
                                            key={item.key}
                                            enabled={notificationSettings[item.key]}
                                            onChange={() => setNotificationSettings({ ...notificationSettings, [item.key]: !notificationSettings[item.key] })}
                                            label={item.label}
                                            description={item.description}
                                            badge={item.badge}
                                        />
                                    ))}
                                </div>
                            </div>

                            <div className="flex justify-end">
                                <button
                                    onClick={handleSaveNotificationSettings}
                                    disabled={saving}
                                    className="px-6 py-3 bg-accent text-white rounded-xl font-semibold shadow-lg shadow-accent/30 hover:shadow-xl hover:shadow-accent/40 transition-all disabled:opacity-50 flex items-center gap-2"
                                >
                                    {saving ? <Loader2 size={20} className="animate-spin no-theme-transition" /> : <Save size={20} />}
                                    Save Preferences
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ─── Templates Tab ──────────────────────────── */}
                    {activeTab === 'templates' && (
                        <div className="space-y-6">
                            {templatesLoading ? (
                                <div className="flex items-center justify-center py-12">
                                    <Loader2 size={24} className="animate-spin text-muted-foreground" />
                                </div>
                            ) : (() => {
                                const typeBadge = {
                                    preboarding: { label: 'Preboarding', className: 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300' },
                                    onboarding: { label: 'Onboarding', className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300' },
                                    active: { label: 'Active', className: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300' },
                                };
                                const allTemplates = templates.sort((a, b) => {
                                    const order = { preboarding: 0, onboarding: 1, active: 2 };
                                    return (order[a.template_type] ?? 3) - (order[b.template_type] ?? 3);
                                });
                                return (
                                    <>
                                        {/* Header */}
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <h3 className="text-lg font-semibold text-foreground">Email Templates</h3>
                                                <p className="text-sm text-muted-foreground">{allTemplates.length} template{allTemplates.length !== 1 ? 's' : ''}</p>
                                            </div>
                                            <div className="relative group">
                                                <button className="flex items-center gap-2 px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors text-sm font-medium shadow-lg shadow-accent/30">
                                                    <Plus size={16} />
                                                    Create Template
                                                </button>
                                                <div className="absolute right-0 mt-1 w-48 bg-card border border-border rounded-xl shadow-xl py-1.5 z-20 hidden group-focus-within:block hover:block">
                                                    {['preboarding', 'onboarding', 'active'].map(type => (
                                                        <button
                                                            key={type}
                                                            onClick={() => navigate(`/admin/settings/templates/new?type=${type}`)}
                                                            className="w-full text-left px-4 py-2 text-sm text-foreground hover:bg-secondary transition-colors flex items-center gap-2.5"
                                                        >
                                                            <span className={`inline-block w-2 h-2 rounded-full ${type === 'preboarding' ? 'bg-blue-500' : type === 'onboarding' ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                                                            {type.charAt(0).toUpperCase() + type.slice(1)}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Template list */}
                                        {allTemplates.length === 0 ? (
                                            <div className="text-center py-12 bg-secondary/50 rounded-xl border border-border border-dashed">
                                                <FileText size={36} className="mx-auto text-muted-foreground mb-3" />
                                                <p className="text-sm font-medium text-foreground mb-1">No templates yet</p>
                                                <p className="text-xs text-muted-foreground">Create your first email template to get started</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-2">
                                                {allTemplates.map((template) => {
                                                    const badge = typeBadge[template.template_type] || typeBadge.onboarding;
                                                    return (
                                                        <div key={template.id} className="flex items-center gap-3 p-3.5 bg-secondary rounded-xl border border-border hover:border-accent/30 transition-colors">
                                                            <button
                                                                onClick={() => handleSetDefault(template.id)}
                                                                className={`flex-shrink-0 ${template.is_default ? 'text-amber-500' : 'text-muted-foreground/30 hover:text-amber-400'} transition-colors`}
                                                                title={template.is_default ? 'Default template' : 'Set as default'}
                                                            >
                                                                <Star size={16} fill={template.is_default ? 'currentColor' : 'none'} />
                                                            </button>

                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2 mb-0.5">
                                                                    <p className="font-medium text-foreground text-sm truncate">{template.name}</p>
                                                                    <span className={`flex-shrink-0 px-2 py-0.5 text-[10px] font-semibold rounded-full ${badge.className}`}>
                                                                        {badge.label}
                                                                    </span>
                                                                </div>
                                                                <p className="text-xs text-muted-foreground truncate">
                                                                    {template.subject}
                                                                    <span className="mx-1.5 text-border">·</span>
                                                                    Edited {new Date(template.updated_at).toLocaleDateString()}
                                                                    {template.created_by_name && ` by ${template.created_by_name}`}
                                                                </p>
                                                            </div>

                                                            <div className="flex items-center gap-1 flex-shrink-0">
                                                                <button
                                                                    onClick={() => navigate(`/admin/settings/templates/${template.id}`)}
                                                                    className="px-3 py-1.5 text-xs font-medium text-accent bg-accent/10 rounded-lg hover:bg-accent/20 transition-colors"
                                                                >
                                                                    Edit
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDuplicateTemplate(template.id)}
                                                                    className="p-1.5 text-muted-foreground hover:text-foreground rounded-lg hover:bg-card transition-colors"
                                                                    title="Duplicate"
                                                                >
                                                                    <Copy size={14} />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDeleteTemplate(template.id)}
                                                                    className="p-1.5 text-muted-foreground hover:text-destructive rounded-lg hover:bg-destructive/10 transition-colors"
                                                                    title="Delete"
                                                                >
                                                                    <Trash2 size={14} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminSettingsPage;
